"use client"

import React, { useState, useEffect } from "react"
import axios from "axios"
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Paper,
  Chip,
  Alert,
  CircularProgress,
} from "@mui/material"
import {
  Campaign as CampaignIcon,
  CheckCircle as CheckCircleIcon,
  Receipt as ReceiptIcon, // Added for transactions
} from "@mui/icons-material"
import { useSelector } from "react-redux"
import { API_URL } from "../../constants"

const UserDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const { userInfo } = useSelector((state) => state.auth)

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true)
        const config = {
          headers: {
            Authorization: `Bearer ${userInfo.token}`,
          },
        }

        const { data } = await axios.get(`${API_URL}/api/dashboard/user`, config)
        setDashboardData(data)
        setLoading(false)
      } catch (error) {
        setError(error.response && error.response.data.message ? error.response.data.message : error.message)
        setLoading(false)
      }
    }

    fetchDashboardData()
  }, [userInfo])

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    )
  }

  if (!dashboardData) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        No dashboard data available.
      </Alert>
    )
  }

  // Initialize with default values to prevent undefined errors
  const stats = dashboardData.stats || {
    totalCampaigns: 0,
    completedCampaigns: 0,
    totalMessages: 0,
    deliveryRate: 0,
    availableCredits: 0,
    usedCredits: 0,
    whatsappNumbers: 0,
  }

  const campaignPerformance = dashboardData.campaignPerformance || {
    completed: 0,
    pending: 0,
    processing: 0,
    failed: 0,
  }

  const recentCampaigns = dashboardData.recentCampaigns || []
  const recentTransactions = dashboardData.recentTransactions || [] // Added for user dashboard

  const messageStats = dashboardData.messageStats || {
    delivered: 0,
    deliveredPercentage: 0,
    read: 0,
    readPercentage: 0,
    responded: 0,
    respondedPercentage: 0,
    failed: 0,
    failedPercentage: 0,
  }

  return (
    <Box>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={12}>
          <Card sx={{ height: "100%", position: "relative", overflow: "visible" }}>
            <Box
              sx={{
                height: 4,
                bgcolor: "primary.main",
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
              }}
            />
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <CampaignIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" component="h3">
                  My Campaigns
                </Typography>
              </Box>
              <Typography variant="h3" component="div" sx={{ fontWeight: "bold", my: 1 }}>
                {stats.totalCampaigns || 0}
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Chip
                  size="small"
                  icon={<CheckCircleIcon fontSize="small" />}
                  label={`${stats.completedCampaigns || 0} completed`}
                  color="success"
                  variant="outlined"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ height: "100%", position: "relative", overflow: "visible" }}>
            <Box
              sx={{
                height: 4,
                bgcolor: "secondary.main",
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
              }}
            />
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <MessageIcon color="secondary" sx={{ mr: 1 }} />
                <Typography variant="h6" component="h3">
                  Messages
                </Typography>
              </Box>
              <Typography variant="h3" component="div" sx={{ fontWeight: "bold", my: 1 }}>
                {(stats.totalMessages || 0).toLocaleString()}
              </Typography>
              
            </CardContent>
          </Card>
        </Grid> */}

        {/* <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ height: "100%", position: "relative", overflow: "visible" }}>
            <Box
              sx={{
                height: 4,
                bgcolor: "success.main",
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
              }}
            />
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <CreditCardIcon color="success" sx={{ mr: 1 }} />
                <Typography variant="h6" component="h3">
                  Credits
                </Typography>
              </Box>
              <Typography variant="h3" component="div" sx={{ fontWeight: "bold", my: 1 }}>
                {stats.availableCredits || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {stats.usedCredits || 0} credits used
              </Typography>
            </CardContent>
          </Card>
        </Grid> */}
      </Grid>

      <Grid container spacing={3}>
        {/* <Grid item xs={12} md={12}>
          <Paper elevation={1} sx={{ borderRadius: 2 }}>
            <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}>
              <Typography variant="h6" component="h2">
                Recent Campaigns
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Your latest campaign activities
              </Typography>
            </Box>
            <List sx={{ p: 0 }}>
              {recentCampaigns && recentCampaigns.length > 0 ? (
                recentCampaigns.map((campaign, index) => {
                  // Determine which icon and color to use based on the campaign status
                  let StatusIcon = CheckCircleIcon
                  let statusColor = "success.main"

                  if (campaign.status === "pending") {
                    StatusIcon = PendingIcon
                    statusColor = "warning.main"
                  } else if (campaign.status === "failed") {
                    StatusIcon = ErrorIcon
                    statusColor = "error.main"
                  }

                  return (
                    <React.Fragment key={index}>
                      {index > 0 && <Divider component="li" />}
                      <ListItem sx={{ py: 2, px: 3, "&:hover": { bgcolor: "action.hover" } }}>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: statusColor }}>
                            <StatusIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={campaign.title || "Untitled Campaign"}
                          secondary={`${campaign.messageCount || 0} messages • ${campaign.deliveryRate || 0}% delivered`}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {campaign.createdAt ? new Date(campaign.createdAt).toLocaleDateString() : "N/A"}
                        </Typography>
                      </ListItem>
                    </React.Fragment>
                  )
                })
              ) : (
                <ListItem>
                  <ListItemText primary="No recent campaigns" />
                </ListItem>
              )}
            </List>
          </Paper>
        </Grid> */}

        <Grid item xs={12} md={6}>
          <Paper elevation={1} sx={{ borderRadius: 2 }}>
            <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}>
              <Typography variant="h6" component="h2">
                Recent Credit Transactions
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Your latest credit activities
              </Typography>
            </Box>
            <List sx={{ p: 0 }}>
              {recentTransactions && recentTransactions.length > 0 ? (
                recentTransactions.map((transaction) => {
                  const sanitize = (text) =>
                    typeof text === "string"
                      ? text
                        .replace(/\s*\$?\s*Report\s+manipulated:\s*(true|false)\s*\$?\s*/gi, "")
                        .trim()
                      : text;


                  const primaryText =
                    transaction.title ||
                    `${transaction.transactionType === "credit" ? "Credit Added" : "Charged"}: ${transaction.amount || 0} ${transaction.creditType || ""
                    } credits`

                  const secondaryText =
                    sanitize(transaction.description) ||
                    [
                      transaction.assignedBy?.name
                        ? `By ${transaction.assignedBy.name}${transaction.assignedBy.role ? ` (${transaction.assignedBy.role})` : ""
                        }`
                        : null,
                      transaction.creditType ? `Type: ${transaction.creditType}` : null,
                    ]
                      .filter(Boolean)
                      .join(" • ")

                  return (
                    <React.Fragment key={transaction._id}>
                      {transaction._id > 0 && <Divider component="li" />}
                      <ListItem sx={{ py: 2, px: 3, "&:hover": { bgcolor: "action.hover" } }}>
                        <ListItemAvatar>
                          <Avatar
                            sx={{ bgcolor: transaction.transactionType === "credit" ? "success.main" : "info.main" }}
                            aria-label={transaction.transactionType === "credit" ? "credit added" : "credit debited"}
                          >
                            <ReceiptIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText primary={primaryText} secondary={secondaryText} />
                        <Typography variant="body2" color="text.secondary">
                          {transaction.createdAt ? new Date(transaction.createdAt).toLocaleDateString() : "N/A"}
                        </Typography>
                      </ListItem>
                    </React.Fragment>
                  )
                })
              ) : (
                <ListItem>
                  <ListItemText primary="No recent transactions" />
                </ListItem>
              )}
            </List>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper elevation={1} sx={{ borderRadius: 2 }}>
            <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}>
              <Typography variant="h6" component="h2">
                Campaign Status Overview
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Status of all your campaigns
              </Typography>
            </Box>
            <Box sx={{ p: 3 }}>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="body2">Completed</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stats.totalCampaigns > 0
                      ? ((stats.completedCampaigns / stats.totalCampaigns) * 100).toFixed(1)
                      : 0}
                    %
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={stats.totalCampaigns > 0 ? (stats.completedCampaigns / stats.totalCampaigns) * 100 : 0}
                  color="success"
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="body2">Pending</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stats.totalCampaigns  && stats.pending > 0  > 0 ? ((stats.pending / stats.totalCampaigns) * 100).toFixed(1) : 0}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={stats.totalCampaigns && stats.pending > 0 ? (stats.pending / stats.totalCampaigns) * 100 : 0}
                  color="warning"
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="body2">Processing</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stats.totalCampaigns && stats.processing > 0 ? ((stats.processing / stats.totalCampaigns) * 100).toFixed(1) : 0}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={stats.totalCampaigns  && stats.processing  > 0 ? (stats.processing / stats.totalCampaigns) * 100 : 0}
                  color="info"
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="body2">Rejected Campaigns</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stats.totalCampaigns  && stats.rejectedCampaigns > 0  ? ((stats.rejectedCampaigns / stats.totalCampaigns) * 100).toFixed(1) : 0}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={stats.totalCampaigns && stats.rejectedCampaigns > 0 ? (stats.rejectedCampaigns / stats.totalCampaigns) * 100 : 0}
                  color="error"
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* <Grid item xs={12}>
          <Paper elevation={1} sx={{ borderRadius: 2 }}>
            <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}>
              <Typography variant="h6" component="h2">
                Message Delivery Statistics
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Performance metrics for your messages
              </Typography>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Metric</TableCell>
                    <TableCell>Count</TableCell>
                    <TableCell>Percentage</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {messageStats && (
                    <>
                      <TableRow hover>
                        <TableCell>Delivered</TableCell>
                        <TableCell>{(messageStats.delivered || 0).toLocaleString()}</TableCell>
                        <TableCell>{messageStats.deliveredPercentage || 0}%</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            icon={<CheckCircleIcon fontSize="small" />}
                            label="Success"
                            color="success"
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow hover>
                        <TableCell>Read</TableCell>
                        <TableCell>{(messageStats.read || 0).toLocaleString()}</TableCell>
                        <TableCell>{messageStats.readPercentage || 0}%</TableCell>
                        <TableCell>
                          <Chip size="small" label="Tracked" color="info" />
                        </TableCell>
                      </TableRow>
                      <TableRow hover>
                        <TableCell>Responded</TableCell>
                        <TableCell>{(messageStats.responded || 0).toLocaleString()}</TableCell>
                        <TableCell>{messageStats.respondedPercentage || 0}%</TableCell>
                        <TableCell>
                          <Chip size="small" label="Engagement" color="secondary" />
                        </TableCell>
                      </TableRow>
                      <TableRow hover>
                        <TableCell>Failed</TableCell>
                        <TableCell>{(messageStats.failed || 0).toLocaleString()}</TableCell>
                        <TableCell>{messageStats.failedPercentage || 0}%</TableCell>
                        <TableCell>
                          <Chip size="small" icon={<ErrorIcon fontSize="small" />} label="Error" color="error" />
                        </TableCell>
                      </TableRow>
                    </>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid> */}
      </Grid>
    </Box>
  )
}

export default UserDashboard
